import { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  Container,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Paper,
  Divider,
  Chip,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  SelectChangeEvent,
  InputAdornment,
} from '@mui/material';
import {
  ArrowBack,
  Edit,
  Save,
  Cancel,
  Delete,
  Visibility,
  VisibilityOff,
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { usersService } from './users.service';
import type { User } from './users.dto';
import { formatDate } from '@/helpers';

import { useToast } from '../../contexts/ToastContext';
import { getPasswordStrengthError } from '../../utils/passwordStrength';
import { isValidPhoneFormat } from '../../utils/phoneValidation';
const EditUser = () => {
  const { showSuccess, showError } = useToast();
  const navigate = useNavigate();
  const { id } = useParams();
  const { token, logout } = useAuth();
  const [formData, setFormData] = useState({
    userCode: '',
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    password: '',
    role: 'end_user',
    // No hardcoded gender - registration never collects it (nor should an
    // edit form silently invent one), so this should reflect "not set"
    // until the real user data loads, not assume Male.
    gender: '',
    status: true,
    createdAt: '',
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deleteDialog, setDeleteDialog] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const genders = [
    { value: 'male', label: 'Male' },
    { value: 'female', label: 'Female' },
    { value: 'other', label: 'Other' },
    { value: 'prefer_not_to_say', label: 'Prefer not to say' },
  ];

  const statuses = [
    { value: true, label: 'Active' },
    { value: false, label: 'Inactive' },
  ];

  // Set up logout callback and fetch data
  useEffect(() => {
    usersService.setLogoutCallback(logout);

    const fetchData = async () => {
      if (!token || !id) return;

      try {
        setLoading(true);
        const user = await usersService.getUser(token, parseInt(id));

        setFormData({
          userCode: user.userCode || 'N/A',
          firstName: user.firstName,
          lastName: user.lastName,
          email: user.email,
          phone: user.phone || '',
          password: '', // Leave empty, will only update if user enters a new password
          role: user.role,
          // user.gender is nullable and commonly is null - coerce to '' so
          // the Select below has a real, matched "nothing selected" value
          // instead of passing null straight through to a MUI component
          // that only knows about the string values in `genders`.
          gender: user.gender || '',
          status: user.status,
          createdAt: user.createdAt || '',
        });
      } catch (err) {
        if (err instanceof Error) {
          showError(err.message);
        } else {
          showError('Failed to fetch user data');
        }
        navigate('/users');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [token, id, navigate, logout]);

  const handleChange =
    (field: keyof typeof formData) =>
    (
      event:
        | React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
        | SelectChangeEvent
    ) => {
      let value: string | boolean = (event.target as HTMLInputElement).value;

      // Special handling for phone field - only allow + and numbers
      if (field === 'phone' && typeof value === 'string') {
        value = value.replace(/[^+0-9]/g, '');
        // Ensure + can only be at the start
        if (value.includes('+')) {
          const plusCount = (value.match(/\+/g) || []).length;
          if (plusCount > 1 || value.indexOf('+') !== 0) {
            value = value.replace(/\+/g, '');
            if ((event.target as HTMLInputElement).value.startsWith('+')) {
              value = '+' + value;
            }
          }
        }
      }

      if (field === 'status') {
        value = value === 'true';
      }
      setFormData({ ...formData, [field]: value });
      if (errors[field as string]) {
        setErrors({ ...errors, [field as string]: '' });
      }
    };

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.firstName.trim()) {
      newErrors.firstName = 'First name is required';
    }

    if (!formData.lastName.trim()) {
      newErrors.lastName = 'Last name is required';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Email is invalid';
    }

    // Password is optional in edit mode, but if provided, must be valid
    if (formData.password) {
      const passwordError = getPasswordStrengthError(formData.password);
      if (passwordError) {
        newErrors.password = passwordError;
      }
    }

    // Validate phone (optional, but if provided should be valid)
    if (formData.phone && !isValidPhoneFormat(formData.phone)) {
      newErrors.phone = 'Phone number can only contain + and numbers';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm() || !token || !id) return;

    setSaving(true);
    try {
      const userData: Partial<User> = {
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        phone: formData.phone,
        role: formData.role,
        status: formData.status,
      };

      // '' means "Not specified" was chosen (or nothing was ever set) -
      // that's not a valid Gender enum value, so it has to be omitted
      // entirely rather than sent as-is (same pattern as AddUser.tsx).
      if (formData.gender) {
        userData.gender = formData.gender as User['gender'];
      }

      // Only include password if it's been changed
      if (formData.password.trim()) {
        userData.password = formData.password;
      }

      await usersService.updateUser(token, parseInt(id), userData);

      showSuccess('User updated successfully!');

      setTimeout(() => {
        navigate('/users');
      }, 1500);
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Failed to update user');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    navigate('/users');
  };

  const handleDelete = async () => {
    if (!token || !id) return;

    try {
      await usersService.deleteUser(token, parseInt(id));
      showSuccess('User deleted successfully!');

      setTimeout(() => {
        navigate('/users');
      }, 1500);
    } catch (err) {
      if (err instanceof Error) {
        showError(err.message);
      } else {
        showError('Failed to delete user');
      }
    }
    setDeleteDialog(false);
  };

  const getStatusColor = (status: boolean) => {
    return status ? 'success' : 'error';
  };

  const getStatusText = (status: boolean) => {
    return status ? 'Active' : 'Inactive';
  };

  if (loading) {
    return (
      <Container maxWidth="lg">
        <Box sx={{ py: 4, textAlign: 'center' }}>
          <CircularProgress />
          <Typography variant="h6" sx={{ mt: 2 }}>
            Loading user data...
          </Typography>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg">
      <Box sx={{ py: 4 }}>
        {/* Header */}
        <Paper elevation={1} sx={{ p: 3, mb: 3 }}>
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            mb={2}
          >
            <Box display="flex" alignItems="center">
              <IconButton onClick={() => navigate('/users')} sx={{ mr: 2 }}>
                <ArrowBack />
              </IconButton>
              <Edit color="primary" sx={{ mr: 1 }} />
              <Typography variant="h4" component="h1">
                Edit User
              </Typography>
            </Box>
            <Chip
              label={getStatusText(formData.status)}
              color={getStatusColor(formData.status)}
              variant="outlined"
            />
          </Box>
          <Typography variant="body1" color="text.secondary">
            Update user information and settings
          </Typography>
        </Paper>

        <Grid container spacing={3}>
          {/* Settings Section */}
          <Grid item xs={12} md={4}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  User Settings
                </Typography>
                <FormControl fullWidth margin="normal">
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={String(formData.status)}
                    label="Status"
                    onChange={handleChange('status')}
                  >
                    {statuses.map(status => (
                      <MenuItem
                        key={String(status.value)}
                        value={String(status.value)}
                      >
                        {status.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <FormControl fullWidth margin="normal">
                  {/* A Select with displayEmpty always shows *some* text in
                      the box - even the "Not specified" placeholder - so
                      the label must always be shrunk to the top, not just
                      when a real value is chosen. Conditioning it on
                      formData.gender left the label overlapping the
                      "Not specified" text whenever gender was unset. */}
                  <InputLabel shrink>Gender</InputLabel>
                  <Select
                    value={formData.gender}
                    label="Gender"
                    displayEmpty
                    onChange={handleChange('gender')}
                  >
                    <MenuItem value="">
                      <em>Not specified</em>
                    </MenuItem>
                    {genders.map(gender => (
                      <MenuItem key={gender.value} value={gender.value}>
                        {gender.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <Divider sx={{ my: 2 }} />
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  User Code
                </Typography>
                <Typography variant="h6" sx={{ textTransform: 'capitalize' }}>
                  #{formData.userCode || 'N/A'}
                </Typography>
                {formData.createdAt && (
                  <>
                    <Divider sx={{ my: 2 }} />
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      gutterBottom
                    >
                      Member since
                    </Typography>
                    <Typography variant="h6">
                      {formatDate(formData.createdAt)}
                    </Typography>
                  </>
                )}
              </CardContent>
            </Card>

            {/* Danger Zone */}
            <Card
              sx={{ mt: 2, border: '1px solid', borderColor: 'error.light' }}
            >
              <CardContent>
                <Typography variant="h6" color="error" gutterBottom>
                  Danger Zone
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 2 }}
                >
                  Once you delete a user, there is no going back. Please be
                  certain.
                </Typography>
                <Button
                  variant="outlined"
                  color="error"
                  startIcon={<Delete />}
                  onClick={() => setDeleteDialog(true)}
                  fullWidth
                >
                  Delete User
                </Button>
              </CardContent>
            </Card>
          </Grid>

          {/* Form Section */}
          <Grid item xs={12} md={8}>
            <Card>
              <CardContent>
                <Box component="form" onSubmit={handleSubmit} noValidate>
                  <Grid container spacing={3}>
                    {/* Basic Information */}
                    <Grid item xs={12}>
                      <Typography variant="h6" gutterBottom>
                        Basic Information
                      </Typography>
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="First Name"
                        value={formData.firstName}
                        onChange={handleChange('firstName')}
                        error={!!errors.firstName}
                        helperText={errors.firstName}
                        required
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Last Name"
                        value={formData.lastName}
                        onChange={handleChange('lastName')}
                        error={!!errors.lastName}
                        helperText={errors.lastName}
                        required
                      />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Email Address"
                        type="email"
                        value={formData.email}
                        onChange={handleChange('email')}
                        error={!!errors.email}
                        helperText={errors.email}
                        required
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Phone Number"
                        value={formData.phone}
                        onChange={handleChange('phone')}
                        error={!!errors.phone}
                        helperText={errors.phone || 'Format: +542121516'}
                        placeholder="+542121516"
                      />
                    </Grid>

                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Password"
                        type={showPassword ? 'text' : 'password'}
                        value={formData.password}
                        onChange={handleChange('password')}
                        error={!!errors.password}
                        helperText={
                          errors.password ||
                          'Leave blank to keep current password'
                        }
                        placeholder="Optional - min 6 chars, incl. uppercase, lowercase & a number"
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">
                              <IconButton
                                aria-label="toggle password visibility"
                                onClick={() => setShowPassword(prev => !prev)}
                                edge="end"
                              >
                                {showPassword ? (
                                  <VisibilityOff />
                                ) : (
                                  <Visibility />
                                )}
                              </IconButton>
                            </InputAdornment>
                          ),
                        }}
                      />
                    </Grid>
                  </Grid>

                  <Divider sx={{ my: 3 }} />

                  <Box display="flex" justifyContent="flex-end" gap={2}>
                    <Button
                      variant="outlined"
                      onClick={handleCancel}
                      startIcon={<Cancel />}
                    >
                      Cancel
                    </Button>
                    <Button
                      type="submit"
                      variant="contained"
                      startIcon={
                        saving ? <CircularProgress size={20} /> : <Save />
                      }
                      disabled={saving}
                      sx={{
                        background:
                          'linear-gradient(45deg, #1976d2 30%, #42a5f5 90%)',
                        '&:hover': {
                          background:
                            'linear-gradient(45deg, #1565c0 30%, #1976d2 90%)',
                        },
                      }}
                    >
                      {saving ? 'Saving...' : 'Save Changes'}
                    </Button>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Box>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialog} onClose={() => setDeleteDialog(false)}>
        <DialogTitle>Delete User</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete "{formData.firstName}{' '}
            {formData.lastName}"? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog(false)}>Cancel</Button>
          <Button onClick={handleDelete} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default EditUser;
